import dayjs from 'dayjs';

import { parseInlineTokens } from '~forms/components/MergeFieldToken';
import { FieldSchema, FormField, MergeDisplayFormField, RichTextFormField } from '~forms/types';

/**
 * Extracts all unique bare merge tokens (e.g. 'client.fullName') from all
 * relevant string fields in a FieldSchema. Used to build the resolve request payload.
 * Scans: label, help_text, default_value for all fields; merge_token for merge_field;
 * content for rich_text.
 */
export function extractFormTokens(schema: FieldSchema): string[] {
  const tokenSet = new Set<string>();

  const collectFromText = (text: string | undefined) => {
    if (!text) return;
    parseInlineTokens(text)
      .filter((seg) => seg.type === 'token')
      .forEach((seg) => tokenSet.add(seg.value));
  };

  for (const field of schema.fields) {
    collectFromText(field.label);
    collectFromText(field.help_text);
    collectFromText(field.default_value);

    if (field.type === 'merge_field') {
      const mf = field as MergeDisplayFormField;
      if (mf.merge_token) {
        tokenSet.add(mf.merge_token);
      }
    }

    if (field.type === 'rich_text') {
      const rt = field as RichTextFormField;
      collectFromText(rt.content);
    }
  }

  return Array.from(tokenSet);
}

/**
 * Resolves all {{merge:<token>}} sentinels in a text string by substituting the
 * resolved value from the provided map. Unknown tokens are replaced with an empty string.
 */
export function resolveTokens(text: string, resolved: Record<string, string>): string {
  return text.replace(/\{\{merge:([^}]+)\}\}/g, (_match, token: string) => resolved[token] ?? '');
}

/**
 * Determines whether a field is editable (i.e. collects a user response).
 * Non-editable types: section_divider, merge_field, rich_text.
 * signature is editable (TAV-3152: captured via SignatureField, not a placeholder).
 * Also excludes read_only fields.
 */
const NON_INPUT_TYPES: FormField['type'][] = ['section_divider', 'merge_field', 'rich_text'];

export function isEditableField(field: FormField): boolean {
  return !field.read_only && !NON_INPUT_TYPES.includes(field.type);
}

/**
 * Builds the completed-form value snapshot: a string value for EVERY field, keyed by field id, with
 * merge tokens resolved to their submission-time values. ALL fields are included — editable answers PLUS
 * read-only fields, merge fields, rich-text content and section dividers — so the saved record is a
 * frozen snapshot, immune to future merge-field or client-data changes. Only the submitted value of each
 * field is stored (never the dropdown/multiple-choice option lists).
 */
const MERGE_SENTINEL = '{{merge:';

export function buildFieldSnapshot(
  fields: FormField[],
  responses: Record<string, string>,
  resolved: Record<string, string>,
): Record<string, string> {
  const snapshot: Record<string, string> = {};
  for (const field of fields) {
    if (isEditableField(field)) {
      snapshot[field.id] = responses[field.id] ?? '';
    } else if (field.type === 'merge_field') {
      snapshot[field.id] = resolved[(field as MergeDisplayFormField).merge_token] ?? '';
    } else if (field.type === 'rich_text') {
      snapshot[field.id] = resolveTokens((field as RichTextFormField).content, resolved);
    } else if (field.type === 'section_divider') {
      snapshot[field.id] = resolveTokens(field.label ?? '', resolved);
    } else if (field.read_only) {
      snapshot[field.id] = resolveTokens(field.default_value ?? '', resolved);
    } else {
      // Any other non-editable field not covered above: store whatever response exists (usually blank).
      snapshot[field.id] = responses[field.id] ?? '';
    }

    // Freeze label/help text too, but only when they embed a merge token — so the read-only view can
    // render them without a live re-resolve. (Dividers store their label as the value above; rich text
    // has no separate label.) Plain labels are read straight from the frozen schema, so we skip them.
    if (field.type !== 'section_divider' && field.type !== 'rich_text') {
      if (field.label?.includes(MERGE_SENTINEL)) snapshot[`label:${field.id}`] = resolveTokens(field.label, resolved);
      if (field.help_text?.includes(MERGE_SENTINEL)) {
        snapshot[`help:${field.id}`] = resolveTokens(field.help_text, resolved);
      }
    }
  }
  return snapshot;
}

/**
 * Resolves a date field's default_value into a concrete `YYYY-MM-DD` value suitable for a
 * native `<input type="date">`. The builder stores either the keywords 'today'/'yesterday'
 * (resolved relative to now at fill time) or an already-ISO custom date. Anything unparseable
 * (or empty) yields '' so the input renders blank rather than showing an invalid keyword.
 */
export function resolveDateDefault(defaultValue: string | undefined): string {
  if (!defaultValue) return '';
  if (defaultValue === 'today') return dayjs().format('YYYY-MM-DD');
  if (defaultValue === 'yesterday') return dayjs().subtract(1, 'day').format('YYYY-MM-DD');
  const parsed = dayjs(defaultValue);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
}
